import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from './AuthContext';

const Notification = ({ message, type }) => (
  <div style={{
    width: '100%',
    marginBottom: '16px',
    padding: '12px',
    borderRadius: '10px',
    textAlign: 'center',
    fontWeight: 600,
    color: type === 'success' ? '#8cffb1' : '#ffb4b4',
    background: type === 'success' ? 'rgba(28,170,89,.16)' : 'rgba(255,55,55,.16)',
    border: type === 'success' ? '1px solid rgba(28,170,89,.35)' : '1px solid rgba(255,55,55,.35)'
  }}>
    {message}
  </div>
);

const LoginPage = () => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [theme, setTheme] = useState({
    title: 'NFLIX2 PLUS',
    logo: '',
    background: '',
    login_button: 'Entrar',
    login_footer: 'Protegido • Não compartilhe suas credenciais.',
    primary_color: '#23aaff'
  });

  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    fetch('/painel/theme_public.php', { cache: 'no-store' })
      .then(res => res.json())
      .then(data => {
        setTheme({
          title: data.title || 'NFLIX2 PLUS',
          logo: data.logo || '',
          background: data.background || '',
          login_button: data.login_button || 'Entrar',
          login_footer: data.login_footer || 'Protegido • Não compartilhe suas credenciais.',
          primary_color: data.primary_color || '#23aaff'
        });
      })
      .catch(err => {
        console.error('Erro ao carregar tema:', err);
      });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNotification(null);
    setIsLoading(true);

    try {
      const success = await login(credentials.username, credentials.password);

      if (success) {
        setNotification({ message: 'Login realizado com sucesso!', type: 'success' });
        setTimeout(() => navigate('/'), 700);
      } else {
        setNotification({ message: 'Credenciais inválidas. Por favor, tente novamente.', type: 'error' });
      }
    } catch (err) {
      console.error('Login error:', err);
      setNotification({
        message: err.response?.data?.message || 'Erro ao fazer login. Por favor, tente novamente.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const primaryColor = theme.primary_color || '#23aaff';

  const pageBackground = theme.background
    ? `linear-gradient(rgba(2,8,18,.82), rgba(2,8,18,.88)), url("${theme.background}") center center / cover no-repeat`
    : 'radial-gradient(circle at center, #08233d, #020711 70%)';

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: pageBackground,
      color: '#fff',
      fontFamily: 'Arial, sans-serif'
    }}>
      <form
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: '520px',
          background: 'rgba(12,21,33,.88)',
          border: `1px solid ${primaryColor}55`,
          boxShadow: `0 0 35px ${primaryColor}44`,
          borderRadius: '18px',
          padding: '38px 32px 28px',
          backdropFilter: 'blur(10px)'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '18px' }}>
          {theme.logo ? (
            <img
              src={theme.logo}
              alt="Logo"
              style={{
                maxWidth: '100px',
                maxHeight: '100px',
                objectFit: 'contain'
              }}
            />
          ) : (
            <div style={{
              margin: '0 auto',
              width: '74px',
              height: '74px',
              borderRadius: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '42px',
              fontWeight: 900,
              color: primaryColor,
              background: `${primaryColor}14`,
              border: `1px solid ${primaryColor}55`,
              textShadow: `0 0 15px ${primaryColor}`
            }}>
              N
            </div>
          )}
        </div>

        <h1 style={{
          textAlign: 'center',
          margin: '0 0 26px',
          fontSize: '28px',
          color: '#bce4ff',
          textShadow: `0 0 14px ${primaryColor}`,
          letterSpacing: '1px'
        }}>
          {theme.title || 'NFLIX2 PLUS'}
        </h1>

        {notification && <Notification message={notification.message} type={notification.type} />}

        <div style={{ marginBottom: '22px' }}>
          <label htmlFor="username" style={{
            display: 'block',
            fontSize: '16px',
            fontWeight: 700,
            marginBottom: '9px',
            color: '#dcecff'
          }}>
            <FaUser /> Usuário
          </label>

          <input
            id="username"
            name="username"
            type="text"
            value={credentials.username}
            onChange={handleChange}
            required
            autoComplete="username"
            placeholder="seu usuário"
            style={{
              width: '100%',
              height: '56px',
              borderRadius: '11px',
              border: '1px solid #314357',
              background: '#1a2431',
              color: '#dcecff',
              fontSize: '18px',
              padding: '0 18px',
              outline: 'none'
            }}
          />
        </div>

        <div style={{ marginBottom: '22px' }}>
          <label htmlFor="password" style={{
            display: 'block',
            fontSize: '16px',
            fontWeight: 700,
            marginBottom: '9px',
            color: '#dcecff'
          }}>
            <FaLock /> Senha
          </label>

          <div style={{ position: 'relative' }}>
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={credentials.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
              placeholder="sua senha"
              style={{
                width: '100%',
                height: '56px',
                borderRadius: '11px',
                border: '1px solid #314357',
                background: '#1a2431',
                color: '#dcecff',
                fontSize: '18px',
                padding: '0 48px 0 18px',
                outline: 'none'
              }}
            />

            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                top: '50%',
                right: '14px',
                transform: 'translateY(-50%)',
                width: '28px',
                height: '28px',
                border: 0,
                background: 'transparent',
                color: '#9fc5e8',
                cursor: 'pointer',
                padding: 0
              }}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          style={{
            width: '100%',
            height: '58px',
            border: 0,
            borderRadius: '13px',
            background: `linear-gradient(180deg, ${primaryColor}, #0c8bf1)`,
            color: '#00111f',
            fontSize: '18px',
            fontWeight: 800,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.75 : 1
          }}
        >
          {isLoading ? 'Entrando...' : (theme.login_button || 'Entrar')}
        </button>

        <div style={{
          textAlign: 'center',
          marginTop: '18px',
          color: '#9fc5e8',
          fontSize: '14px'
        }}>
          {theme.login_footer || 'Protegido • Não compartilhe suas credenciais.'}
        </div>
      </form>
    </div>
  );
};

export default LoginPage;
